import styles from '../DynamicFormBuilder.module.css';

const FIELD_LABEL_CLASS = 'form-label small text-uppercase text-muted d-flex flex-column gap-1 mb-0';
const INPUT_CLASS = 'form-control form-control-sm';
const SELECT_CLASS = 'form-select form-select-sm';

const isBlank = (value) => value === null || value === undefined || value === '';

// A schema entry's `key` may be a one-level path (`parent.child`) so an option
// whose renderer contract is an object can still be edited as separate inputs.
function getPath(config, key) {
  const [parentKey, childKey] = key.split('.');
  const top = (config || {})[parentKey];
  if (childKey === undefined) return top;
  return top && typeof top === 'object' ? top[childKey] : undefined;
}

function coerceNumber(value, { integer }) {
  if (value === '') return null;
  const n = integer ? parseInt(value, 10) : parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function renderControl(entry, value, onChange) {
  const { key, kind, options } = entry;
  const id = `cfg-${key}`;

  switch (kind) {
    case 'boolean':
      return (
        <input
          id={id}
          type="checkbox"
          className="form-check-input"
          checked={Boolean(value)}
          onChange={(e) => onChange(key, e.target.checked)}
        />
      );
    case 'integer':
    case 'number':
      return (
        <input
          id={id}
          type="number"
          className={INPUT_CLASS}
          step={kind === 'integer' ? 1 : 'any'}
          value={value ?? ''}
          onChange={(e) => onChange(key, coerceNumber(e.target.value, { integer: kind === 'integer' }))}
        />
      );
    case 'select': {
      // A schema entry may list options as bare strings (value doubles as the
      // label) or as { value, label } for tokens that need a human label.
      const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
      return (
        <select
          id={id}
          className={SELECT_CLASS}
          value={value ?? ''}
          onChange={(e) => onChange(key, e.target.value || null)}
        >
          <option value="">(default)</option>
          {opts.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label ?? opt.value}</option>
          ))}
        </select>
      );
    }
    case 'textarea':
      return (
        <textarea
          id={id}
          className={INPUT_CLASS}
          rows={entry.rows ?? 3}
          value={value ?? ''}
          onChange={(e) => onChange(key, e.target.value === '' ? null : e.target.value)}
        />
      );
    case 'text':
    default:
      return (
        <input
          id={id}
          type="text"
          className={INPUT_CLASS}
          value={value ?? ''}
          onChange={(e) => onChange(key, e.target.value === '' ? null : e.target.value)}
        />
      );
  }
}

export default function ConfigForm({ schema, config, onConfigChange }) {
  if (!schema || schema.length === 0) {
    return <p className="m-0 small text-muted fst-italic">No type-specific settings.</p>;
  }

  const setKey = (key, value) => {
    onConfigChange((prev) => {
      const next = { ...(prev || {}) };
      const [parentKey, childKey] = key.split('.');
      if (childKey === undefined) {
        if (isBlank(value)) delete next[key];
        else next[key] = value;
        return next;
      }
      // The parent may hold a stale non-object — an older builder wrote a bare
      // string where the renderer expects `{ add, remove }` — so replace it
      // rather than spreading a string into character keys.
      const parent = next[parentKey];
      const usable = parent && typeof parent === 'object' && !Array.isArray(parent);
      const branch = { ...(usable ? parent : {}) };
      if (isBlank(value)) delete branch[childKey];
      else branch[childKey] = value;
      // Drop the parent once its last child goes, rather than leaving `{}` behind.
      if (Object.keys(branch).length === 0) delete next[parentKey];
      else next[parentKey] = branch;
      return next;
    });
  };

  // `showIf` hides an option that cannot apply to the current config — e.g. the
  // action-bar labels on a group with no repeat bounds, which renders no action bar.
  const visible = schema.filter((entry) => !entry.showIf || entry.showIf(config || {}));
  const boolEntries = visible.filter((e) => e.kind === 'boolean');
  const otherEntries = visible.filter((e) => e.kind !== 'boolean');

  return (
    <div className="d-flex flex-column gap-3">
      {otherEntries.length > 0 && (
        <div className={styles.editorGrid}>
          {otherEntries.map((entry) => (
            <label key={entry.key} className={FIELD_LABEL_CLASS} htmlFor={`cfg-${entry.key}`}>
              <span>{entry.label || entry.key}</span>
              {renderControl(entry, getPath(config, entry.key), setKey)}
            </label>
          ))}
        </div>
      )}

      {boolEntries.length > 0 && (
        <fieldset className={`border rounded p-2 ${styles.flagSet}`}>
          <legend className="form-text small text-uppercase text-muted px-1 float-none w-auto">Config flags</legend>
          {boolEntries.map((entry) => {
            // `default: true` flags persist only the non-default (an explicit
            // `false`); unchecking a normal flag just drops the key.
            const value = getPath(config, entry.key);
            const on = entry.default === true ? value !== false : Boolean(value);
            return (
              <div key={entry.key} className="checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) =>
                      setKey(
                        entry.key,
                        entry.default === true
                          ? (e.target.checked ? null : false)
                          : (e.target.checked || null),
                      )
                    }
                  />
                  <span>{entry.label || entry.key}</span>
                </label>
              </div>
            );
          })}
        </fieldset>
      )}
    </div>
  );
}
